import { useCallback, useLayoutEffect, useState } from "react";
import useResizeObserver from "use-resize-observer";

// Each item in the work history has height 70px, so for 4 = 280 px
const biggestMaxHeight = 280;
const additionalPadding = 15;

export type TPosition = {
    top: number;
    left: number;
    maxHeight: number;
};

export type TUsePopver = (
    isBalanceChart: boolean,
    parentWidth?: number | null,
    parentHeight?: number | null,
    xElement?: number,
    yElement?: number,
    scrollTop?: number
) => {
    position: TPosition;
    ref: (instance: HTMLDivElement | null) => void;
    refHeader: (instance: HTMLDivElement | null) => void;
    refBody: (instance: HTMLDivElement | null) => void;
    recalculatePosition: () => void;
};

const usePopoverPosition: TUsePopver = (isBalanceChart, parentWidth, parentHeight, xElement, yElement, scrollTop) => {
    const { ref, width = -1, height = -1 } = useResizeObserver<HTMLDivElement>();
    const { ref: refHeader, height: heightHeader = -1 } = useResizeObserver<HTMLDivElement>();
    const { ref: refBody, height: heightList = -1 } = useResizeObserver<HTMLDivElement>();
    const [position, setPosition] = useState<TPosition>({ top: -1, left: -1, maxHeight: -1 });

    const recalculatePosition = useCallback(() => {
        if (parentWidth == null || parentHeight == null || xElement == null || yElement == null) {
            return;
        }

        const yElementWithScroll = yElement - (scrollTop ?? 0);

        // Orientation
        const onRight = xElement > parentWidth / 2;
        const onTop = yElementWithScroll < parentHeight / 2;

        // Default values for loading
        let left = onRight ? xElement - 200 : xElement + additionalPadding;
        let top = onTop ? yElementWithScroll : yElementWithScroll - 200;
        let maxHeight = biggestMaxHeight;

        if (width > 0 && height > 0 && heightHeader > 0 && heightList > 0) {
            const componentHeightWithoutList = height - heightList;

            left = onRight ? Math.max(xElement - width, 0) : xElement + additionalPadding;

            if (onTop) {
                const hasOverflow = yElementWithScroll + height > parentHeight;
                top = yElementWithScroll;
                maxHeight = hasOverflow
                    ? Math.min(parentHeight - yElementWithScroll - componentHeightWithoutList, biggestMaxHeight)
                    : Math.min(heightList, biggestMaxHeight);
            } else {
                const hasOverflow = yElementWithScroll - height < 0;
                top = hasOverflow
                    ? yElementWithScroll -
                      componentHeightWithoutList -
                      Math.min(yElementWithScroll - componentHeightWithoutList, biggestMaxHeight)
                    : yElementWithScroll - height;
                maxHeight = biggestMaxHeight;
            }
        }

        setPosition({ top, left, maxHeight });
    }, [width, height, heightHeader, heightList, parentWidth, parentHeight, xElement, yElement, scrollTop]);

    useLayoutEffect(() => {
        recalculatePosition();
    }, [recalculatePosition]);

    return { position, ref, refHeader, refBody, recalculatePosition };
};

export default usePopoverPosition;
